import type { LookbackOption, FilterOption } from './types';

export const POLLING_INTERVAL_MS = 30000;

export const LOOKBACK_OPTIONS: LookbackOption[] = [
  { id: '1', label: 'Last 1 hour' },
  { id: '6', label: 'Last 6 hours' },
  { id: '24', label: 'Last 24 hours' },
  { id: '48', label: 'Last 48 hours' },
  { id: '72', label: 'Last 72 hours' },
  { id: '168', label: 'Last 7 days' },
  { id: '720', label: 'Last 30 days' },
];

export const ALL_OPTION_ID = 'all';

export const STATE_LABELS: Record<string, string> = {
  pending: 'Pending',
  running: 'Running',
  completed: 'Completed',
  skipped: 'Skipped',
  held: 'Held',
  failed: 'Failed',
};

export const STATE_OPTIONS: FilterOption[] = [
  { id: ALL_OPTION_ID, label: 'All States' },
  { id: 'pending', label: 'Pending' },
  { id: 'running', label: 'Running' },
  { id: 'completed', label: 'Completed' },
  { id: 'skipped', label: 'Skipped' },
  { id: 'held', label: 'Held' },
  { id: 'failed', label: 'Failed' },
];

// Keyed and ordered to mirror InventoryRecompute::REASONS, which is what populates the toolbar
// dropdown below. A reason missing from here still renders as its raw code, but it gets no filter
// option, so a row carrying it cannot be found.
export const REASON_LABELS: Record<string, string> = {
  UNIT_FROZEN_NEEDS_REVIEW: 'Unit frozen, needs review',
  AUTOMATION_OFF: 'Automation off',
  NO_DEPENDENT_UNITS: 'No dependent units',
  UNRESOLVABLE_FORMATION_DEPTH: 'Unresolvable formation depth',
  NO_PROMOTED_VERSION_FOR_ZONE: 'No promoted version for zone',
  NOTHING_TO_PUBLISH: 'Nothing to publish',
};

export const REASON_OPTIONS: FilterOption[] = [
  { id: ALL_OPTION_ID, label: 'All Reasons' },
  ...Object.entries(REASON_LABELS).map(([id, label]) => ({ id, label })),
];

// `triggeredBy` carries the class name of whatever upstream write asked for the recompute. A
// carrier missing from this map still renders, as its raw class name, so a new one costs nothing.
export const TRIGGERED_BY_LABELS: Record<string, string> = {
  'DevelopmentArea': 'Unit',
  'PermitHeader': 'Permit',
  'StructureGridCollection': 'Structure Grid Collection',
  'SourceWell::SurveyGeometry': 'Well Survey Geometry',
  'TypeCurve::HeaderDetail': 'Type Curve Version',
  'WellboreGeometryWorkspace': 'Pre-Drill Wellbore Geometry',
  'TypeCurveScenario::FormationMapping': 'Formation Mapping',
  'WellLandingAnalysisResult': 'Landing Analysis',
  // Not a carrier. The one-time pass that stands inventory up for an area posts through the same
  // job, and naming it here is what puts it in the Triggers dropdown alongside the real carriers.
  'Inventory::Backfill': 'Initial backfill',
};

export const TRIGGERED_BY_OPTIONS: FilterOption[] = [
  { id: ALL_OPTION_ID, label: 'All Triggers' },
  ...Object.entries(TRIGGERED_BY_LABELS).map(([id, label]) => ({ id, label })),
];

// A recompute that has not settled is one a reader may see change while the page is open, which is
// what makes background polling worth paying for.
export const UNSETTLED_STATES: string[] = ['pending', 'running'];

// The unit count that sits behind a run count, rendered as the StatsBar tag's suffix.
//
// Runs covering every unit in their area are called out separately rather than counted as zero:
// they have no number to add, and folding them in would make a real backlog look empty.
export function unitSuffix(unitCount: number, areaWideRuns: number): string {
  const parts: string[] = [];
  if (unitCount > 0) parts.push(`${unitCount} units`);
  if (areaWideRuns > 0) parts.push(`${areaWideRuns} area-wide`);

  return parts.length > 0 ? ` (${parts.join(', ')})` : '';
}
